<template>
  <div>
    <v-card class="ma-2">
      <v-card-title class="d-flex align-center">
        <div class="mx-auto d-flex align-center">
          <h1 class="d-inline-block mb-0">Error Logs</h1>
          <EnvironmentTag variant="flat" size="large" class="ml-4" />
        </div>
      </v-card-title>
      <v-card-text>
        <v-row align="center">
          <v-col cols="12" sm="6" md="3" class="pb-0">
            <v-date-input
              v-model="dateRange.start"
              label="Start Date"
              prepend-icon="mdi-calendar"
              :display-format="formatISODate"
              density="compact"
              :error-messages="startDateError"
              @update:model-value="validateDates"
            ></v-date-input>
          </v-col>
          <v-col cols="12" sm="6" md="3" class="pb-0">
            <v-date-input
              v-model="dateRange.end"
              label="End Date"
              prepend-icon="mdi-calendar"
              :display-format="formatISODate"
              density="compact"
              :error-messages="endDateError"
              @update:model-value="validateDates"
            ></v-date-input>
          </v-col>
          <v-col cols="12" sm="6" md="3" class="ml-auto">
            <v-text-field
              v-model="search"
              prepend-icon="mdi-magnify"
              label="Search"
              single-line
              hide-details
              density="compact"
              class="search-field"
              variant="outlined"
              rounded
              bg-color="surface"
            ></v-text-field>
          </v-col>
        </v-row>
        <!-- Summary Section and Toggle -->
        <div class="d-flex justify-space-between align-center">
          <div
            class="summary-section"
            style="padding: 16px; border: 1px solid #ccc; margin-bottom: 24px"
          >
            <h3>Summary For Selected Logs</h3>
            <div class="d-flex flex-column gap-2">
              <div class="d-flex align-center">
                <span class="mr-2">Total Errors:</span>
                <v-chip color="red" size="small" variant="flat">
                  {{ filteredErrorLogs.length }}
                </v-chip>
                <span class="mx-2">Unique Clients:</span>
                <v-chip
                  color="orange"
                  size="small"
                  variant="flat"
                  @click="showAffected('clients', null, true)"
                >
                  {{ uniqueClients }}
                </v-chip>
                <span class="mx-2">Unique Users:</span>
                <v-chip
                  color="purple"
                  size="small"
                  variant="flat"
                  @click="showAffected('users', null, true)"
                >
                  {{ uniqueUsers }}
                </v-chip>
              </div>
            </div>
          </div>
          <div class="ml-auto">
            <v-switch
              v-model="groupSimilarErrors"
              label="Group Similar Errors"
              color="primary"
              hide-details
              density="compact"
            ></v-switch>
          </div>
        </div>

        <v-data-table
          :headers="headers"
          :items="filteredErrorLogs"
          class="elevation-1"
          :loading="loading"
          loading-text="Loading error logs..."
          :sort-by="[{ key: 'created_at', order: 'desc' }]"
        >
          <template #[`item.created_at`]="{ item }">
            <div class="text-left text-muted">
              {{ formatDate(item.created_at) }}
            </div>
          </template>
          <template #[`item.message`]="{ item }">
            <div class="text-left text-truncate" style="max-width: 500px">
              {{ item.message }}
            </div>
          </template>
          <template #[`item.file`]="{ item }">
            <div class="text-left text-muted">{{ item.file }}</div>
          </template>
          <template #[`item.affected_clients`]="{ item }">
            <v-chip
              color="orange"
              size="small"
              variant="flat"
              @click="
                showAffected(
                  'clients',
                  this.groupSimilarErrors
                    ? item.affected_clients
                    : item.client_id
                      ? 1
                      : 0,
                  false,
                  item,
                )
              "
            >
              {{
                this.groupSimilarErrors
                  ? item.affected_clients.size
                  : item.client_id
                    ? 1
                    : 0
              }}
            </v-chip>
          </template>
          <template #[`item.affected_users`]="{ item }">
            <v-chip
              color="purple"
              size="small"
              variant="flat"
              @click="
                showAffected(
                  'users',
                  this.groupSimilarErrors
                    ? item.affected_users
                    : item.user_id
                      ? 1
                      : 0,
                  false,
                  item,
                )
              "
            >
              {{
                this.groupSimilarErrors
                  ? item.affected_users.size
                  : item.user_id
                    ? 1
                    : 0
              }}
            </v-chip>
          </template>
          <template #[`item.environment`]="{ item }">
            <div class="text-left text-muted">{{ item.environment }}</div>
          </template>
          <template #[`item.actions`]="{ item }">
            <div class="text-right">
              <v-btn
                size="small"
                color="primary"
                variant="text"
                :to="{
                  path: `/devops/devops-logs/error-log-details/${item.id}`,
                  query: {
                    startDate: dateRange.start,
                    endDate: dateRange.end,
                    search: search,
                    groupSimilarErrors: String(groupSimilarErrors),
                  },
                }"
              >
                View Details
              </v-btn>
            </div>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <DynamicDialog
      :show="showAffectedModal"
      @update:show="showAffectedModal = $event"
      @dialogOk="showAffectedModal = false"
      @dialogCancel="showAffectedModal = false"
      :cancelBtnHidden="true"
      maxWidth="600"
    >
      <template v-slot:title>
        {{ affectedType === "clients" ? "Affected Clients" : "Affected Users" }}
      </template>

      <template v-slot:content>
        <v-card-text>
          <v-list>
            <v-list-item
              v-for="entity in affectedEntities"
              :key="entity.id"
              :title="entity.name"
              :subtitle="entity.description"
            >
              <template v-slot:prepend>
                <v-avatar color="primary" size="32">
                  <span class="text-white">{{ entity.initials }}</span>
                </v-avatar>
              </template>
            </v-list-item>
          </v-list>
        </v-card-text>
      </template>
    </DynamicDialog>
  </div>
</template>

<script>
import { VDateInput } from "vuetify/labs/VDateInput";
import EnvironmentTag from "@/components/EnvironmentTag.vue";
import DynamicDialog from "@/components/DynamicDialog.vue";
import { systemMessages } from "@/mixins/SystemMessages";
import axios from "axios";

const API_URL = process.env.VUE_APP_API_URL || "";

export default {
  name: "DevopsErrorLogs",
  mixins: [systemMessages],
  components: {
    VDateInput,
    EnvironmentTag,
    DynamicDialog,
  },
  props: {},
  data: () => ({
    environment: process.env.VUE_APP_ENVIRONMENT,
    envName: {
      dev: "local",
      stg: "staging",
      datalake: "datalake",
      uat: "uat",
      prod: "production",
    },
    headers: [
      {
        title: "Timestamp",
        key: "created_at",
        sortable: true,
        align: "start",
        sortDirection: "desc",
      },
      {
        title: "Message Preview",
        key: "message",
        sortable: true,
        align: "start",
      },
      { title: "File", key: "file", sortable: true, align: "start" },
      {
        title: "Affected Clients",
        key: "affected_clients",
        sortable: true,
        align: "start",
      },
      {
        title: "Affected Users",
        key: "affected_users",
        sortable: true,
        align: "start",
      },
      {
        title: "Environment",
        key: "environment",
        sortable: true,
        align: "start",
      },
      { title: "Actions", key: "actions", sortable: false, align: "end" },
    ],
    errorLogs: [],
    loading: false,
    dateRange: {
      start: new Date(Date.now() - 24 * 60 * 60 * 1000)
        .toISOString()
        .substr(0, 10),
      end: new Date().toISOString().substr(0, 10),
    },
    startDateError: "",
    endDateError: "",
    search: "",
    groupSimilarErrors: false,
    showAffectedModal: false,
    affectedType: "",
    affectedEntities: [],
  }),
  watch: {
    dateRange: {
      handler() {
        this.fetchErrorLogs();
      },
      deep: true,
    },
  },
  mounted() {
    // Initialize state from query parameters if they exist
    if (this.$route.query.startDate) {
      this.dateRange.start = this.$route.query.startDate;
    }
    if (this.$route.query.endDate) {
      this.dateRange.end = this.$route.query.endDate;
    }
    if (this.$route.query.search) {
      this.search = this.$route.query.search;
    }
    if (this.$route.query.groupSimilarErrors !== undefined) {
      this.groupSimilarErrors = this.$route.query.groupSimilarErrors === "true";
    }
    this.fetchErrorLogs();
  },
  computed: {
    filteredErrorLogs() {
      // First filter by date range and search
      const startDate = new Date(this.dateRange.start);
      const endDate = new Date(this.dateRange.end);
      endDate.setHours(23, 59, 59, 999);

      let logs = this.errorLogs.filter((log) => {
        const logDate = new Date(log.created_at);
        const isInDateRange = logDate >= startDate && logDate <= endDate;
        const searchLower = this.search.toLowerCase();
        const matchesSearch =
          !this.search ||
          Object.values(log).some((value) =>
            String(value).toLowerCase().includes(searchLower),
          );
        return isInDateRange && matchesSearch;
      });

      // If grouping is enabled, group the filtered logs by first 100 chars of message
      if (this.groupSimilarErrors) {
        const groupedLogs = new Map();
        logs.forEach((log) => {
          const messageComparison = log.message.substring(0, 100);
          const key = `${messageComparison}-${log.file}`;
          if (!groupedLogs.has(key)) {
            groupedLogs.set(key, {
              message: log.message,
              file: log.file,
              line: log.line,
              created_at: log.created_at,
              affected_clients: new Set(),
              affected_users: new Set(),
            });
          }
          const group = groupedLogs.get(key);
          if (log.id) group.id = log.id;
          if (log.client_id) group.affected_clients.add(log.client_id);
          if (log.user_id) group.affected_users.add(log.user_id);
        });

        logs = Array.from(groupedLogs.values());
      }

      return logs;
    },
    totalErrors() {
      // Return the length of filteredErrorLogs
      return this.filteredErrorLogs.length;
    },
    uniqueUsers() {
      if (this.groupSimilarErrors) {
        const userCount = new Set(
          this.filteredErrorLogs.flatMap((log) => [...log.affected_users]),
        ).size;

        return userCount;
      }
      const userSet = new Set(this.filteredErrorLogs.map((log) => log.user_id));
      return userSet.size;
    },
    uniqueClients() {
      if (this.groupSimilarErrors) {
        const clientCount = new Set(
          this.filteredErrorLogs.flatMap((log) => [...log.affected_clients]),
        ).size;

        return clientCount;
      }
      const clientSet = new Set(
        this.filteredErrorLogs.map((log) => log.client_id),
      );
      return clientSet.size;
    },
  },
  methods: {
    showAffected(type, count, summary = false, rowData = null) {
      this.affectedType = type;
      let entities;

      if (this.groupSimilarErrors && !summary) {
        // Get all logs with the same message preview and file (if grouping is enabled)
        const currentLog = this.filteredErrorLogs.find((log) => {
          return (
            log[type === "clients" ? "affected_clients" : "affected_users"] ===
            count
          );
        });

        //show list of affected clients/users for grouped error logs
        const ids =
          currentLog[
            type === "clients" ? "affected_clients" : "affected_users"
          ];
        // Create entities array from IDs
        entities = Array.from(ids).map((id) => {
          const log = this.errorLogs.find(
            (l) => l[type === "clients" ? "client_id" : "user_id"] === id,
          );
          const name = log?.[type === "clients" ? "client_name" : "user_name"];
          const displayName = name
            ? name
            : `${type === "clients" ? "Client" : "User"} ${id}`;
          return {
            id,
            name: displayName,
            description: `ID: ${id}`,
            initials: displayName.substring(0, 2),
          };
        });
      } else if (summary) {
        //for summary section create entities from filtered logs
        let ids;

        if (this.groupSimilarErrors) {
          ids = new Set([
            ...this.filteredErrorLogs.flatMap((log) =>
              type === "clients"
                ? [...log.affected_clients]
                : [...log.affected_users],
            ),
          ]);
        } else {
          ids = new Set(
            this.filteredErrorLogs.flatMap(
              (log) => log[type === "clients" ? "client_id" : "user_id"],
            ),
          );
        }

        entities = Array.from(ids).map((id) => {
          const log = this.errorLogs.find(
            (l) => l[type === "clients" ? "client_id" : "user_id"] === id,
          );
          const name = log?.[type === "clients" ? "client_name" : "user_name"];
          const displayName = name
            ? name
            : `${type === "clients" ? "Client" : "User"} ${id}`;
          return {
            id,
            name: displayName,
            description: `ID: ${id}`,
            initials: displayName.substring(0, 2),
          };
        });
      } else {
        // For non-grouped view, get the current log and create entity from it
        const currentLog = this.filteredErrorLogs.find((log) => {
          return log["id"] === rowData["id"];
        });

        if (currentLog) {
          entities = [
            {
              id: currentLog[type === "clients" ? "client_id" : "user_id"],
              name:
                currentLog[type === "clients" ? "client_name" : "user_name"] ||
                `${type === "clients" ? "Client" : "User"} ${currentLog[type === "clients" ? "client_id" : "user_id"]}`,
              description: `ID: ${currentLog[type === "clients" ? "client_id" : "user_id"]}`,
              initials: (
                currentLog[type === "clients" ? "client_name" : "user_name"] ||
                currentLog[
                  type === "clients" ? "client_id" : "user_id"
                ].toString()
              ).substring(0, 2),
            },
          ];
        } else {
          entities = [];
        }
      }

      this.affectedEntities = entities;
      this.showAffectedModal = true;
    },
    validateDates() {
      const startDate = new Date(this.dateRange.start);
      const endDate = new Date(this.dateRange.end);

      // Reset errors
      this.startDateError = "";
      this.endDateError = "";

      if (startDate > endDate) {
        this.startDateError = "Start date cannot be after end date";
        this.endDateError = "End date cannot be before start date";
        return false;
      }
      return true;
    },
    async fetchErrorLogs() {
      if (!this.validateDates()) {
        return;
      }

      this.loading = true;
      try {
        const startDate = new Date(this.dateRange.start);
        const endDate = new Date(this.dateRange.end);
        endDate.setHours(23, 59, 59, 999);

        // Calculate interval in days
        const diffTime = Math.abs(endDate - startDate);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        const interval = `${diffDays} days`;

        const response = await axios.get(`${API_URL}/error-logs`, {
          params: {
            environment:
              this.environment === "uat"
                ? `${this.envName["uat"]},${this.envName["prod"]}`
                : this.envName[this.environment],
            interval,
            returnColumns: [
              "id",
              "environment",
              "created_at",
              "message",
              "file",
              "client_id",
              "user_id",
            ],
            withUserClientDetail: true,
          },
        });
        this.errorLogs = response.data;
      } catch (error) {
        this.errorMsg({
          title: "Error",
          message: "Failed to fetch error logs: " + error,
        });
      } finally {
        this.loading = false;
      }
    },
    formatDate(date) {
      return new Date(date).toLocaleString();
    },
    formatISODate(date) {
      if (!date) return "";
      const d = new Date(date);
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    },
  },
};
</script>

<style scoped>
.error-stats {
  margin-top: 16px;
}
.summary-section {
  margin-top: 16px;
  padding: 16px;
  border: 1px solid #ccc;
}
.search-field {
  margin-top: 0;
}

.search-field :deep(.v-field) {
  border-radius: 24px;
}

.search-field :deep(.v-field__input) {
  padding-left: 16px;
  padding-right: 16px;
}

:deep(.v-data-table) .v-data-table__td {
  padding-left: 4px !important;
  padding-right: 4px !important;
}

:deep(.v-data-table) {
  padding-left: 4px;
  padding-right: 4px;
}

.text-muted {
  color: rgba(0, 0, 0, 0.6);
}
</style>
